import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import { fetchClientSummaryByDate } from '@src/api';
import api from '@src/api/clients';
import { formatDateToYMD, getCurrentMonthDates } from '@src/utils/dateUtils';

const MAPPING_TYPE_OPTIONS = [
  { key: 'email', label: 'Email' },
  { key: 'sftp', label: 'SFTP' },
  { key: 'api', label: 'API' },
  { key: 'google_sheet', label: 'Google Sheet' },
];

const MAPPING_MODE_OPTIONS = [
  { key: 'all', label: 'All Clients' },
  { key: 'with', label: 'With Mapping' },
  { key: 'without', label: 'Without Mapping' },
];

const CLIENT_FIELDS = [
  'id',
  'name',
  'slug',
  'parent_company',
  'parent_company_name',
  'mapping_types',
].join(',');

const MAX_CLIENT_META_PAGES = 50;

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  if (!denom) return 0;
  return num / denom;
};

const normalizeMappingType = (value = '') => {
  const normalized = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
  if (normalized === 'google' || normalized === 'google_sheets' || normalized === 'gsheet') {
    return 'google_sheet';
  }
  if (normalized.startsWith('google_sheet')) {
    return 'google_sheet';
  }
  if (normalized.startsWith('email')) return 'email';
  if (normalized.startsWith('sftp')) return 'sftp';
  if (normalized.startsWith('api')) return 'api';
  return normalized;
};

const getMappingTypes = (rawTypes = []) =>
  Array.from(
    new Set(
      (Array.isArray(rawTypes) ? rawTypes : [])
        .map((type) => normalizeMappingType(type))
        .filter((type) => ['email', 'sftp', 'api', 'google_sheet'].includes(type))
    )
  );

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;

  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: {
        page,
        page_size: 250,
        ordering: 'name',
        fields: CLIENT_FIELDS,
      },
    });
    const data = response?.data;
    if (Array.isArray(data)) {
      allClients.push(...data);
      break;
    }

    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);

    if (!data?.next) {
      break;
    }
    page += 1;
  }

  return allClients;
}

const METRIC_COLUMNS = [
  { key: 'spend', label: 'Spend', type: 'currency' },
  { key: 'leads', label: 'Leads', type: 'count' },
  { key: 'cpl', label: 'CPL', type: 'currency' },
  { key: 'selfBook', label: 'Self Book', type: 'count' },
  { key: 'cpSelfBook', label: 'CP Self Book', type: 'currency' },
  { key: 'accepted', label: 'Accepted', type: 'count' },
  { key: 'acceptedRate', label: 'Accepted Rate', type: 'ratio' },
  { key: 'success', label: 'Success', type: 'count' },
  { key: 'successRate', label: 'Success Rate', type: 'ratio' },
  { key: 'outOfArea', label: 'Out of Area', type: 'count' },
  { key: 'outOfAreaRate', label: 'Out of Area Rate', type: 'ratio' },
  { key: 'booked', label: 'Booked', type: 'count' },
  { key: 'cpBooked', label: 'CP Booked', type: 'currency' },
  { key: 'setRate', label: 'Set Rate', type: 'ratio' },
  { key: 'issues', label: 'Issues', type: 'count' },
  { key: 'cpIssue', label: 'CP Issue', type: 'currency' },
  { key: 'issueRate', label: 'Issue Rate', type: 'ratio' },
  { key: 'leadToIssue', label: 'Lead To Issue', type: 'ratio' },
  { key: 'demos', label: 'Demos', type: 'count' },
  { key: 'cpDemo', label: 'CP Demo', type: 'currency' },
  { key: 'demoRate', label: 'Demo Rate', type: 'ratio' },
  { key: 'grossSold', label: 'Gross Sold', type: 'count' },
  { key: 'netSold', label: 'Net Sold', type: 'count' },
  { key: 'netCloseRate', label: 'Net Close Rate', type: 'ratio' },
  { key: 'netAmount', label: 'Net Amount', type: 'currency' },
  { key: 'avgDeal', label: 'Avg Deal', type: 'currency' },
  { key: 'costOfMarketing', label: 'Cost of Marketing', type: 'ratio' },
];

const computeDerivedMetrics = (base = {}) => {
  const spend = safeNumber(base.spend);
  const leads = safeNumber(base.leads);
  const selfBook = safeNumber(base.selfBook);
  const accepted = safeNumber(base.accepted);
  const success = safeNumber(base.success);
  const outOfArea = safeNumber(base.outOfArea);
  const booked = safeNumber(base.booked);
  const issues = safeNumber(base.issues);
  const scheduled = safeNumber(base.scheduled);
  const demos = safeNumber(base.demos);
  const grossSold = safeNumber(base.grossSold);
  const netSold = safeNumber(base.netSold);
  const netAmount = safeNumber(base.netAmount);

  return {
    spend,
    leads,
    cpl: safeDiv(spend, leads),
    selfBook,
    cpSelfBook: safeDiv(spend, selfBook),
    accepted,
    acceptedRate: safeDiv(accepted, leads),
    success,
    successRate: safeDiv(success, leads),
    outOfArea,
    outOfAreaRate: safeDiv(outOfArea, leads),
    booked,
    cpBooked: safeDiv(spend, booked),
    setRate: safeDiv(booked, leads),
    issues,
    cpIssue: safeDiv(spend, issues),
    issueRate: safeDiv(issues, scheduled),
    leadToIssue: safeDiv(issues, leads),
    demos,
    cpDemo: safeDiv(spend, demos),
    demoRate: safeDiv(demos, issues),
    grossSold,
    netSold,
    netCloseRate: safeDiv(netSold, demos),
    netAmount,
    avgDeal: safeDiv(netAmount, netSold),
    costOfMarketing: safeDiv(spend, netAmount),
  };
};

const renderMetricValue = (value, type) => {
  if (type === 'currency') {
    return <DollarFormatter value={value} />;
  }
  if (type === 'ratio') {
    return <PercentageFormatter value={value} />;
  }
  return safeNumber(value).toLocaleString();
};

export default function ClientPerformanceAggregator() {
  const [defaultStart, defaultEnd] = React.useMemo(() => getCurrentMonthDates(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [selectedParentCompanies, setSelectedParentCompanies] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [selectedMappingTypes, setSelectedMappingTypes] = React.useState(
    MAPPING_TYPE_OPTIONS.map((option) => option.key)
  );
  const [mappingMode, setMappingMode] = React.useState('all');

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ordering: 'client__name,date',
        };

        const [summaryResp, clients] = await Promise.all([
          fetchClientSummaryByDate(payload),
          fetchAllClientMetadata(),
        ]);

        const summaryRows = Array.isArray(summaryResp?.data)
          ? summaryResp.data
          : Array.isArray(summaryResp?.data?.results)
            ? summaryResp.data.results
            : [];

        const clientMetaById = new Map();
        clients.forEach((client = {}) => {
          const id = Number(client.id);
          if (!Number.isFinite(id)) return;
          clientMetaById.set(id, {
            id,
            slug: client.slug || '',
            name: client.name || '',
            parentCompanyId: client.parent_company ? String(client.parent_company) : '',
            parentCompanyName: client.parent_company_name || 'No Parent Company',
            mappingTypes: getMappingTypes(client.mapping_types),
          });
        });

        const aggregateByClient = new Map();
        summaryRows.forEach((row = {}) => {
          const clientId = Number(row.client_id);
          if (!Number.isFinite(clientId)) return;

          const fallbackName = row.client__name || row.client_name || `Client ${clientId}`;
          const meta = clientMetaById.get(clientId) || {
            id: clientId,
            slug: '',
            name: fallbackName,
            parentCompanyId: '',
            parentCompanyName: 'No Parent Company',
            mappingTypes: [],
          };

          if (!aggregateByClient.has(clientId)) {
            aggregateByClient.set(clientId, {
              clientId,
              clientName: meta.name || fallbackName,
              slug: meta.slug || '',
              parentCompanyId: meta.parentCompanyId || '',
              parentCompanyName: meta.parentCompanyName || 'No Parent Company',
              mappingTypes: meta.mappingTypes || [],
              spend: 0,
              leads: 0,
              selfBook: 0,
              accepted: 0,
              success: 0,
              outOfArea: 0,
              booked: 0,
              issues: 0,
              scheduled: 0,
              demos: 0,
              grossSold: 0,
              netSold: 0,
              netAmount: 0,
            });
          }

          const bucket = aggregateByClient.get(clientId);
          bucket.spend += safeNumber(row.total_spend_sum);
          bucket.leads += safeNumber(row.total_leads_sum);
          bucket.selfBook += safeNumber(row.self_books_sum);
          bucket.accepted += safeNumber(row.accepted_sum);
          bucket.success += safeNumber(row.success_sum);
          bucket.outOfArea += safeNumber(row.out_of_areas_count_sum);
          bucket.booked += safeNumber(row.booked_sum);
          bucket.issues += safeNumber(row.issues_sum);
          bucket.scheduled += safeNumber(row.scheduled_sum);
          bucket.demos += safeNumber(row.demos_sum);
          bucket.grossSold += safeNumber(row.gross_sales_sum);
          bucket.netSold += safeNumber(row.net_sales_sum);
          bucket.netAmount += safeNumber(row.net_amount_sum);
        });

        const nextRows = Array.from(aggregateByClient.values())
          .map((row) => ({
            ...row,
            ...computeDerivedMetrics(row),
          }))
          .sort((a, b) => b.spend - a.spend);

        if (isMounted) {
          setRows(nextRows);
        }
      } catch (err) {
        console.error('Failed to load client performance aggregate:', err);
        if (isMounted) {
          setRows([]);
          setError('Unable to load aggregated client performance data.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [startDate, endDate]);

  const parentCompanyOptions = React.useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const key = row.parentCompanyId || '__none__';
      const name = row.parentCompanyName || 'No Parent Company';
      if (!map.has(key)) {
        map.set(key, { id: key, name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const clientOptions = React.useMemo(() => {
    const parentFilter = new Set(selectedParentCompanies);
    return rows
      .filter((row) => {
        if (parentFilter.size === 0) return true;
        const rowParent = row.parentCompanyId || '__none__';
        return parentFilter.has(rowParent);
      })
      .map((row) => ({
        id: String(row.clientId),
        name: row.clientName,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, selectedParentCompanies]);

  React.useEffect(() => {
    const allowed = new Set(clientOptions.map((option) => option.id));
    setSelectedClients((prev) => prev.filter((id) => allowed.has(id)));
  }, [clientOptions]);

  const filteredRows = React.useMemo(() => {
    const parentFilter = new Set(selectedParentCompanies);
    const clientFilter = new Set(selectedClients);
    const mappingTypeFilter = new Set(selectedMappingTypes);

    const matchesMappingMode = (row) => {
      const mappingTypes = Array.isArray(row.mappingTypes) ? row.mappingTypes : [];
      const hasRelevantMapping =
        mappingTypeFilter.size === 0
          ? mappingTypes.length > 0
          : mappingTypes.some((type) => mappingTypeFilter.has(type));

      if (mappingMode === 'with') return hasRelevantMapping;
      if (mappingMode === 'without') return !hasRelevantMapping;
      return true;
    };

    return rows.filter((row) => {
      const rowParent = row.parentCompanyId || '__none__';
      if (parentFilter.size > 0 && !parentFilter.has(rowParent)) return false;
      if (clientFilter.size > 0 && !clientFilter.has(String(row.clientId))) return false;
      if (!matchesMappingMode(row)) return false;
      return true;
    });
  }, [rows, selectedParentCompanies, selectedClients, selectedMappingTypes, mappingMode]);

  const totals = React.useMemo(() => {
    const base = filteredRows.reduce(
      (acc, row) => {
        acc.spend += safeNumber(row.spend);
        acc.leads += safeNumber(row.leads);
        acc.selfBook += safeNumber(row.selfBook);
        acc.accepted += safeNumber(row.accepted);
        acc.success += safeNumber(row.success);
        acc.outOfArea += safeNumber(row.outOfArea);
        acc.booked += safeNumber(row.booked);
        acc.issues += safeNumber(row.issues);
        acc.scheduled += safeNumber(row.scheduled);
        acc.demos += safeNumber(row.demos);
        acc.grossSold += safeNumber(row.grossSold);
        acc.netSold += safeNumber(row.netSold);
        acc.netAmount += safeNumber(row.netAmount);
        return acc;
      },
      {
        spend: 0,
        leads: 0,
        selfBook: 0,
        accepted: 0,
        success: 0,
        outOfArea: 0,
        booked: 0,
        issues: 0,
        scheduled: 0,
        demos: 0,
        grossSold: 0,
        netSold: 0,
        netAmount: 0,
      }
    );
    return computeDerivedMetrics(base);
  }, [filteredRows]);

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  return (
    <Page title="Client Performance Aggregator" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardHeader
            title="Client Performance Aggregator"
            subheader="Aggregated summary-model metrics across all clients with filters for parent company, client, and mapping status."
          />
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateRangeChange}
                  style={{ width: '100%' }}
                />
              </Box>

              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="parent-company-filter-label">Parent Companies</InputLabel>
                <Select
                  labelId="parent-company-filter-label"
                  multiple
                  value={selectedParentCompanies}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedParentCompanies(typeof value === 'string' ? value.split(',') : value);
                  }}
                  input={<OutlinedInput label="Parent Companies" />}
                  renderValue={(selected) => {
                    if (!selected.length) return 'All Parent Companies';
                    return parentCompanyOptions
                      .filter((option) => selected.includes(option.id))
                      .map((option) => option.name)
                      .join(', ');
                  }}
                >
                  {parentCompanyOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="client-filter-label">Clients</InputLabel>
                <Select
                  labelId="client-filter-label"
                  multiple
                  value={selectedClients}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedClients(typeof value === 'string' ? value.split(',') : value);
                  }}
                  input={<OutlinedInput label="Clients" />}
                  renderValue={(selected) => {
                    if (!selected.length) return 'All Clients';
                    return clientOptions
                      .filter((option) => selected.includes(option.id))
                      .map((option) => option.name)
                      .join(', ');
                  }}
                >
                  {clientOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <ToggleButtonGroup
                value={mappingMode}
                exclusive
                size="small"
                onChange={(_, value) => {
                  if (value) {
                    setMappingMode(value);
                  }
                }}
              >
                {MAPPING_MODE_OPTIONS.map((option) => (
                  <ToggleButton key={option.key} value={option.key}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <FormControl size="small" sx={{ minWidth: 320 }}>
                <InputLabel id="mapping-type-filter-label">Mapping Types</InputLabel>
                <Select
                  labelId="mapping-type-filter-label"
                  multiple
                  value={selectedMappingTypes}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedMappingTypes(typeof value === 'string' ? value.split(',') : value);
                  }}
                  input={<OutlinedInput label="Mapping Types" />}
                  renderValue={(selected) => {
                    if (!selected.length) return 'Any Mapping Type';
                    return MAPPING_TYPE_OPTIONS.filter((option) => selected.includes(option.key))
                      .map((option) => option.label)
                      .join(', ');
                  }}
                >
                  {MAPPING_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary">
                Showing {filteredRows.length} client{filteredRows.length === 1 ? '' : 's'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Client Performance Table"
            subheader="Same core performance metrics used in client dashboard rollups."
          />
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: '74vh' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 2600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper', minWidth: 220 }}>
                        Client
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Parent Company</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Mappings</TableCell>
                      {METRIC_COLUMNS.map((column) => (
                        <TableCell key={column.key} sx={{ minWidth: 120 }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={METRIC_COLUMNS.length + 3} align="center" sx={{ py: 6 }}>
                          No clients match the selected filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {filteredRows.map((row) => (
                          <TableRow key={row.clientId} hover>
                            <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'background.paper' }}>
                              <Typography
                                component={RouterLink}
                                to={`/clients/${row.slug || row.clientId}/dashboard`}
                                sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 600 }}
                              >
                                {row.clientName}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.parentCompanyName || 'No Parent Company'}</TableCell>
                            <TableCell>
                              {row.mappingTypes.length
                                ? row.mappingTypes
                                    .map((type) => MAPPING_TYPE_OPTIONS.find((option) => option.key === type)?.label || type)
                                    .join(', ')
                                : 'None'}
                            </TableCell>
                            {METRIC_COLUMNS.map((column) => (
                              <TableCell key={column.key}>
                                {renderMetricValue(row[column.key], column.type)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'background.paper', fontWeight: 700 }}>
                            Total
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>-</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>-</TableCell>
                          {METRIC_COLUMNS.map((column) => (
                            <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                              {renderMetricValue(totals[column.key], column.type)}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Page>
  );
}
